/**
 * Student classroom redeem/list adapters. Application code imports from `src/api.ts`.
 */
import type {
  ClassroomAssistantSummary,
  ClassroomAssistantsResponse,
  ClassroomRedeemResponse,
  ClassroomTokenCreateResponse,
  ClassroomTokenListResponse,
  ClassroomTokenSummary,
} from '../types/backend';
import { DOMAIN_ERROR_MESSAGES_DA } from '../types/backend';
import { parseBackendUtcTimestamp } from '../lib/liveIntegration';
import { ApiError, CONTRACT_MESSAGE, USE_LIVE, encodeApiSegment, fetchJson, mapAdminError, resolveDashboardAuth } from './http';

let mockGrantActive = false;

/** Test-only: reset mock grant state between isolated cases. */
export function resetMockClassroomGrantForTests(): void {
  mockGrantActive = false;
}

export async function redeemClassroomCode(code: string): Promise<{ expires_at: string }> {
  if (!USE_LIVE) {
    mockGrantActive = true;
    return {
      expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    };
  }

  const data = await fetchJson<ClassroomRedeemResponse>('/chat/classroom/redeem', {
    session: true,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  try {
    parseBackendUtcTimestamp(data.expires_at);
  } catch {
    throw new ApiError({
      httpStatus: 502,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }

  return { expires_at: data.expires_at };
}

export async function listClassroomAssistants(): Promise<ClassroomAssistantSummary[]> {
  if (!USE_LIVE) {
    if (!mockGrantActive) {
      throw new ApiError({
        httpStatus: 404,
        code: 'classroom_token_invalid',
        userMessage: DOMAIN_ERROR_MESSAGES_DA.classroom_token_invalid,
      });
    }
    return [];
  }

  const data = await fetchJson<ClassroomAssistantsResponse>('/chat/classroom/assistants', {
    session: true,
  });
  if (!Array.isArray(data.assistants)) {
    throw new ApiError({
      httpStatus: 502,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }
  return data.assistants;
}

const ADMIN_CLASSROOM_LIST_ERROR = 'Kunne ikke hente klassekoder.';
const ADMIN_CLASSROOM_CREATE_ERROR = 'Kunne ikke oprette klassekode.';
const ADMIN_CLASSROOM_REVOKE_ERROR = 'Kunne ikke tilbagekalde klassekoden.';

export async function mintClassroomToken(): Promise<{ id: string; code: string; expires_at: string }> {
  if (!USE_LIVE) {
    return {
      id: 'mock-token-id',
      code: 'ABC123',
      expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    };
  }

  try {
    const data = await fetchJson<ClassroomTokenCreateResponse>('/chat/admin/classroom-tokens', {
      method: 'POST',
      ...resolveDashboardAuth(),
    });
    try {
      parseBackendUtcTimestamp(data.expires_at);
    } catch {
      throw new ApiError({
        httpStatus: 502,
        code: null,
        userMessage: CONTRACT_MESSAGE,
      });
    }
    return data;
  } catch (error) {
    throw mapAdminError(error, ADMIN_CLASSROOM_CREATE_ERROR);
  }
}

export async function listClassroomTokens(): Promise<ClassroomTokenSummary[]> {
  if (!USE_LIVE) {
    return [];
  }

  try {
    const data = await fetchJson<ClassroomTokenListResponse>('/chat/admin/classroom-tokens', {
      ...resolveDashboardAuth(),
    });
    if (!Array.isArray(data.tokens)) {
      throw new ApiError({
        httpStatus: 502,
        code: null,
        userMessage: CONTRACT_MESSAGE,
      });
    }
    return data.tokens;
  } catch (error) {
    throw mapAdminError(error, ADMIN_CLASSROOM_LIST_ERROR);
  }
}

export async function revokeClassroomToken(id: string): Promise<void> {
  if (!USE_LIVE) {
    return;
  }

  try {
    await fetchJson<void>(`/chat/admin/classroom-tokens/${encodeApiSegment(id)}`, {
      method: 'DELETE',
      ...resolveDashboardAuth(),
    });
  } catch (error) {
    throw mapAdminError(error, ADMIN_CLASSROOM_REVOKE_ERROR);
  }
}
